/**
 * Data Export Modal
 * 
 * GDPR-compliant data export interface allowing users to download all their data.
 * Includes format selection and export status tracking.
 */

import React, { useState, useEffect } from 'react';
import { Button } from '../ui/Button';
import { Download, FileText, Database, Clock, CheckCircle, AlertCircle, X } from 'lucide-react';
import { gdprService, GDPRStatusResponse } from '../../services/gdprService';
import { toast } from 'sonner';

interface DataExportModalProps {
  isOpen: boolean;
  onClose: () => void;
}

type ExportFormat = 'json' | 'csv' | 'both';

interface ExportRequest {
  format: ExportFormat;
  includeFiles: boolean;
}

export default function DataExportModal({ isOpen, onClose }: DataExportModalProps) {
  const [isLoading, setIsLoading] = useState(false);
  const [exportFormat, setExportFormat] = useState<ExportFormat>('json');
  const [includeFiles, setIncludeFiles] = useState(false);
  const [gdprStatus, setGdprStatus] = useState<GDPRStatusResponse | null>(null);
  const [statusLoading, setStatusLoading] = useState(true);

  // Load GDPR status when modal opens
  useEffect(() => {
    if (isOpen) {
      loadGDPRStatus();
    }
  }, [isOpen]);

  const loadGDPRStatus = async () => {
    setStatusLoading(true);
    try {
      const status = await gdprService.getGDPRStatus();
      setGdprStatus(status);
    } catch (error) {
      console.error('Failed to load GDPR status:', error);
      toast.error('Failed to load export status');
    } finally {
      setStatusLoading(false);
    }
  };

  const handleExportRequest = async () => {
    // TESTING: Rate limiting disabled for development
    // if (!gdprStatus?.canRequestExport) {
    //   toast.error('You can only request a data export once per 24 hours');
    //   return;
    // }

    setIsLoading(true);
    try {
      const result = await gdprService.requestDataExport({
        format: exportFormat,
        includeFiles
      });

      toast.success('Data export prepared successfully!');
      toast.info('Download will start automatically...');

      // Start download
      await gdprService.downloadExportFile(result.downloadUrl);
      
      // Refresh status
      await loadGDPRStatus();
      
      onClose();
    } catch (error) {
      console.error('Data export failed:', error);
      toast.error(error instanceof Error ? error.message : 'Failed to export data');
    } finally {
      setIsLoading(false);
    }
  };

  const handleDownloadExisting = async (downloadUrl: string) => {
    try {
      await gdprService.downloadExportFile(downloadUrl);
      toast.success('Download started');
    } catch (error) {
      console.error('Download failed:', error);
      toast.error('Failed to download file');
    }
  };

  const getFormatIcon = (format: ExportFormat) => {
    switch (format) {
      case 'json': return <FileText className="w-4 h-4" />;
      case 'csv': return <Database className="w-4 h-4" />;
      case 'both': return <Download className="w-4 h-4" />;
    }
  };

  const getFormatDescription = (format: ExportFormat) => {
    switch (format) {
      case 'json': return 'Structured data format, best for developers';
      case 'csv': return 'Spreadsheet format, easy to view and analyze';
      case 'both': return 'Both JSON and CSV formats included';
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-3">
              <Download className="w-6 h-6 text-blue-600" />
              <h2 className="text-2xl font-bold text-gray-900">Export Your Data</h2>
            </div>
            <button
              onClick={onClose}
              className="p-2 hover:bg-gray-100 rounded-full transition-colors"
            >
              <X className="w-5 h-5 text-gray-500" />
            </button>
          </div>
          <p className="text-gray-600 mb-6">
            Download a complete copy of all your data in compliance with GDPR regulations.
          </p>

            {statusLoading ? (
              <div className="flex items-center justify-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                <span className="ml-3 text-gray-600">Loading export status...</span>
              </div>
            ) : (
              <div className="space-y-6">
                {/* Recent Exports */}
                {gdprStatus && gdprStatus.recentExports.length > 0 && (
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                    <h3 className="font-semibold text-blue-900 mb-3 flex items-center gap-2">
                      <Clock className="w-4 h-4" />
                      Recent Exports
                    </h3>
                    <div className="space-y-2">
                      {gdprStatus.recentExports.map((exp, index) => (
                        <div key={index} className="flex items-center justify-between bg-white rounded p-3">
                          <div className="flex items-center gap-3">
                            <CheckCircle className="w-4 h-4 text-green-600" />
                            <div>
                              <p className="text-sm font-medium text-gray-900">
                                Exported {gdprService.formatExportDate(exp.exportedAt)}
                              </p>
                              {exp.expiresAt && (
                                <p className="text-xs text-gray-600">
                                  {gdprService.getTimeUntilExpiration(exp.expiresAt)}
                                </p>
                              )}
                            </div>
                          </div>
                          {exp.expiresAt && new Date(exp.expiresAt) > new Date() && (
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleDownloadExisting('placeholder-url')}
                              className="text-xs"
                            >
                              Download
                            </Button>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Rate Limit Warning - DISABLED FOR TESTING */}
                {false && gdprStatus && !gdprStatus.canRequestExport && (
                  <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                    <div className="flex items-start gap-3">
                      <AlertCircle className="w-5 h-5 text-amber-600 mt-0.5 flex-shrink-0" />
                      <div>
                        <h3 className="font-medium text-amber-900">Export Rate Limit</h3>
                        <p className="text-sm text-amber-800 mt-1">
                          You can only request a data export once per 24 hours. Please wait before requesting another export.
                        </p>
                      </div>
                    </div>
                  </div>
                )}

                {/* Export Options */}
                <div className="space-y-4">
                  <h3 className="font-semibold text-gray-900">Export Options</h3>
                  
                  {/* Format Selection */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-3">
                      Data Format
                    </label>
                    <div className="grid grid-cols-1 gap-3">
                      {(['json', 'csv', 'both'] as ExportFormat[]).map((format) => (
                        <label
                          key={format}
                          className={`flex items-center p-3 border rounded-lg cursor-pointer transition-colors ${
                            exportFormat === format
                              ? 'border-blue-500 bg-blue-50'
                              : 'border-gray-200 hover:border-gray-300'
                          }`}
                        >
                          <input
                            type="radio"
                            name="format"
                            value={format}
                            checked={exportFormat === format}
                            onChange={(e) => setExportFormat(e.target.value as ExportFormat)}
                            className="sr-only"
                          />
                          <div className="flex items-center gap-3 w-full">
                            {getFormatIcon(format)}
                            <div>
                              <p className="font-medium text-gray-900 capitalize">{format}</p>
                              <p className="text-sm text-gray-600">{getFormatDescription(format)}</p>
                            </div>
                          </div>
                        </label>
                      ))}
                    </div>
                  </div>

                  {/* Include Files Option */}
                  <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                    <input
                      type="checkbox"
                      id="includeFiles"
                      checked={includeFiles}
                      onChange={(e) => setIncludeFiles(e.target.checked)}
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                    />
                    <label htmlFor="includeFiles" className="flex-1">
                      <p className="font-medium text-gray-900">Include processed files</p>
                      <p className="text-sm text-gray-600">
                        Include links to your uploaded and processed images (larger download)
                      </p>
                    </label>
                  </div>
                </div>

                {/* Data Types Included */}
                <div className="bg-gray-50 rounded-lg p-4">
                  <h4 className="font-medium text-gray-900 mb-3">Data Included in Export</h4>
                  <div className="grid grid-cols-2 gap-2 text-sm text-gray-700">
                    <div className="flex items-center gap-2">
                      <CheckCircle className="w-3 h-3 text-green-600" />
                      Profile information
                    </div>
                    <div className="flex items-center gap-2">
                      <CheckCircle className="w-3 h-3 text-green-600" />
                      Credit history
                    </div>
                    <div className="flex items-center gap-2">
                      <CheckCircle className="w-3 h-3 text-green-600" />
                      Upload history
                    </div>
                    <div className="flex items-center gap-2">
                      <CheckCircle className="w-3 h-3 text-green-600" />
                      API usage data
                    </div>
                    <div className="flex items-center gap-2">
                      <CheckCircle className="w-3 h-3 text-green-600" />
                      Transaction records
                    </div>
                    <div className="flex items-center gap-2">
                      <CheckCircle className="w-3 h-3 text-green-600" />
                      Account settings
                    </div>
                  </div>
                </div>
              </div>
            )}

          {/* Footer */}
          <div className="mt-6 flex justify-end gap-3 pt-4 border-t border-gray-200">
            <Button
              variant="outline"
              onClick={onClose}
              disabled={isLoading}
            >
              Cancel
            </Button>
            <Button
              onClick={handleExportRequest}
              disabled={isLoading || statusLoading}
              className="bg-blue-600 hover:bg-blue-700"
            >
              {isLoading ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  Preparing Export...
                </>
              ) : (
                <>
                  <Download className="w-4 h-4 mr-2" />
                  Export Data
                </>
              )}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
